import { forwardRef, useCallback } from 'react';
import { motion, useMotionValue, useSpring, useReducedMotion } from 'motion/react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const SPRING = { stiffness: 260, damping: 22, mass: 0.5 };
const MAGNET_STRENGTH = 0.25;
const MAGNET_MAX = 8;

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

/**
 * Primary CTA — accent-green, magnetic hover, arrow slide, border glow.
 * Renders <a>, <Link>, or <button> based on props (href / to / neither).
 * `arrow` (default true) toggles the trailing chevron.
 * `size="sm"` gives the compact nav variant.
 */
const SignalCta = forwardRef(function SignalCta(
  { children, href, to, target, rel, onClick, type, arrow = true, size, variant, className = '', ...rest },
  ref
) {
  const reduce = useReducedMotion();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, SPRING);
  const sy = useSpring(y, SPRING);

  const handleMove = useCallback(
    (e) => {
      if (reduce) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      x.set(clamp((e.clientX - cx) * MAGNET_STRENGTH, -MAGNET_MAX, MAGNET_MAX));
      y.set(clamp((e.clientY - cy) * MAGNET_STRENGTH, -MAGNET_MAX, MAGNET_MAX));
    },
    [reduce, x, y]
  );

  const handleLeave = useCallback(() => {
    x.set(0);
    y.set(0);
  }, [x, y]);

  const cls = `fa-signal-cta ${size === 'sm' ? 'fa-signal-cta--sm' : ''} ${variant === 'ghost' ? 'fa-signal-cta--ghost' : ''} ${className}`.trim();

  const inner = (
    <>
      <span className="fa-signal-cta__label">{children}</span>
      {arrow && (
        <span className="fa-signal-cta__arrow" aria-hidden="true">
          <ArrowRight size={size === 'sm' ? 14 : 16} strokeWidth={2.2} />
        </span>
      )}
    </>
  );

  const commonProps = {
    ref,
    className: cls,
    onPointerMove: handleMove,
    onPointerLeave: handleLeave,
    style: { x: sx, y: sy },
    ...rest,
  };

  if (to) {
    return (
      <motion.span {...commonProps} style={{ ...commonProps.style, display: 'inline-flex' }}>
        <Link to={to} onClick={onClick} className="fa-signal-cta__inner">
          {inner}
        </Link>
      </motion.span>
    );
  }
  if (href) {
    return (
      <motion.a href={href} target={target} rel={rel} onClick={onClick} {...commonProps}>
        {inner}
      </motion.a>
    );
  }
  return (
    <motion.button type={type || 'button'} onClick={onClick} {...commonProps}>
      {inner}
    </motion.button>
  );
});

export default SignalCta;
